import React, { FunctionComponent, useEffect, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { equals, mapObjIndexed } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import { IContact } from '../../../../common/models/contact/IContact';
import { IUpdateContact } from '../../../../common/models/contact/IUpdateContact';
import { IAppState } from '../../../../common/models/store/IAppState';
import { reqBuilderStep2ValidationSchema } from '../../../../common/validation/yup';
import Icon from '../../../../components/Icon';
import ModalWindow from '../../../../components/ModalWindow';
import RegularButton from '../../../../components/RegularButton';
import {
  setContactInfoErrorRoutine,
  resetContactIsSavedRoutine,
  saveContactInfoRoutine
} from '../../routines';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import Input from '../../../../components/Input';
import styles from './styles.module.scss';

interface IProps {
  isShown: boolean;
  contact: IContact;
  isSaved: boolean;
  isSaving: boolean;
  contactInfoError: boolean;
  openReminder: IBindingAction;
  onHide: IBindingAction;
  saveContact: IBindingCallback1<IUpdateContact>;
  setContactError: IBindingCallback1<boolean>;
  resetIsSaved: IBindingAction;
}

const EditContactModal: FunctionComponent<IProps> = ({
  isShown,
  contact,
  isSaved,
  isSaving,
  contactInfoError,
  onHide,
  saveContact,
  openReminder,
  resetIsSaved,
  setContactError
}) => {
  const [isEmailChanged, setIsEmailChanged] = useState(false);

  const initialValues = {
    firstName: contact.firstName,
    lastName: contact.lastName,
    email: contact.email || '',
    phoneNumber: contact.phoneNumber || ''
  };

  type FormValues = typeof initialValues;

  const onCancel = (reminder?: boolean) => {
    setIsEmailChanged(false);
    setContactError(false);
    resetIsSaved();
    if (reminder) {
      openReminder();
    } else {
      onHide();
    }
  };

  const getTrimmedValues = (values: FormValues) => mapObjIndexed(val => val.trim(), values);

  const onSave = (values: FormValues) => {
    const trimmedValues = getTrimmedValues(values);
    if (trimmedValues.email && trimmedValues.email !== initialValues.email) {
      setIsEmailChanged(true);
    }

    saveContact({
      ...trimmedValues,
      id: contact.id,
      email: trimmedValues.email || null,
      phoneNumber: trimmedValues.phoneNumber || null
    });
  };

  const onChange = (val: string, field: keyof FormValues, { values, setFieldValue }: FormikProps<FormValues>) => {
    if (contactInfoError && val.trim() !== values.email.trim()) {
      setContactError(false);
    }
    setFieldValue(field, val);
  };

  useEffect(() => {
    if (isSaved) {
      onCancel(isEmailChanged);
    }
  }, [isSaved]);

  return (
    <ModalWindow contentClassName="modal-wrapper small-modal" onHide={() => onCancel()} isShown={isShown}>
      <LoaderWrapper transparent loading={isSaving}>
        <Formik
          validationSchema={reqBuilderStep2ValidationSchema}
          initialValues={initialValues}
          onSubmit={onSave}
          validateOnMount
        >
          {(props: FormikProps<FormValues>) => (
            <Form className={styles.editModal}>
              <header className="custom-modal-header">
                <FormattedMessage id="contactProfile.candidate.edit" defaultMessage="Edit contact information" />
              </header>
              <main className={styles.modalBody}>
                <Input
                  type="text"
                  label={{ id: 'firstName', defaultMessage: 'First name' }}
                  errorIntl={props.errors.firstName}
                  onChange={val => props.setFieldValue('firstName', val)}
                  size="lg"
                  variant="underlined"
                  value={props.values.firstName}
                  className={styles.inputContainer}
                />
                <Input
                  type="text"
                  label={{ id: 'lastName', defaultMessage: 'Last name' }}
                  errorIntl={props.errors.lastName}
                  onChange={val => props.setFieldValue('lastName', val)}
                  size="lg"
                  variant="underlined"
                  value={props.values.lastName}
                  className={styles.inputContainer}
                />
                <Input
                  type="email"
                  label={{ id: 'emailAddress', defaultMessage: 'Email address' }}
                  errorIntl={contactInfoError
                    ? { id: 'contactProfile.candidate.editError',
                      defaultMessage: 'Contact with such data already exists' }
                    : props.errors.email}
                  onChange={val => onChange(val, 'email', props)}
                  value={props.values.email}
                  size="lg"
                  variant="underlined"
                  className={styles.inputContainer}
                />
                <Input
                  type="phoneNumber"
                  label={{ id: 'phoneNumber', defaultMessage: 'Phone number' }}
                  errorIntl={contactInfoError
                    ? { id: 'contactProfile.candidate.editError',
                      defaultMessage: 'Contact with such data already exists' }
                    : props.errors.phoneNumber}
                  onChange={val => onChange(val, 'phoneNumber', props)}
                  value={props.values.phoneNumber}
                  size="lg"
                  variant="underlined"
                  dropdownPosition="top"
                />
              </main>
              <footer className="modal-footer-bttns">
                <RegularButton
                  textIntlId="cancel"
                  defaultMessage="Cancel"
                  onClick={() => onCancel()}
                  variant="tertiary"
                />
                <RegularButton
                  textIntlId="save"
                  defaultMessage="Save"
                  icon={<Icon icon="check" />}
                  disabled={!props.isValid
                    || contactInfoError
                    || isSaving
                    || equals(getTrimmedValues(props.values), initialValues)}
                  onClick={props.submitForm}
                />
              </footer>
            </Form>
          )}
        </Formik>
      </LoaderWrapper>
    </ModalWindow>
  );
};

const mapStateToProps = ({
  contactProfile: {
    contact: { contact, isContactSaved, isSavingContact, contactInfoError }
  }
}: IAppState) => ({ contact, isSaved: isContactSaved, isSaving: isSavingContact, contactInfoError });

const mapDispatchToProps = {
  saveContact: saveContactInfoRoutine,
  setContactError: setContactInfoErrorRoutine,
  resetIsSaved: resetContactIsSavedRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(EditContactModal);
